import posthog from 'posthog-js';

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const ENVIRONMENT = process.env.REACT_APP_ENV || 'development';

let enabled = false;

export const initAnalytics = () => {
  if (!KEY) return;                 // no key -> silent no-op everywhere
  posthog.init(KEY, {
    api_host: HOST,
    defaults: '2026-05-30',

    // We capture pageviews manually so we can normalize /leads/1234 -> /leads/:id
    capture_pageview: false,
    capture_pageleave: true,        // needed for time-on-page

    // Behavioural capture — the whole point of this integration
    autocapture: true,
    capture_heatmaps: true,
    capture_dead_clicks: true,

    // PRIVACY: never record the text content of autocaptured elements.
    // On this app that text is patient names, phone numbers, and clinical notes.
    // Element attributes stay ON so `data-attr` labels still work.
    mask_all_text: true,
    mask_all_element_attributes: false,

    // Only create person profiles for logged-in users
    person_profiles: 'identified_only',

    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '.ph-mask',
    },

    loaded: () => { enabled = true; },
  });
  enabled = true;
  posthog.register({ environment: ENVIRONMENT });
};

export const identifyUser = (userInfo, locationInfo) => {
  if (!enabled || !locationInfo) return;
  const companyId = locationInfo.company?.id;

  posthog.identify(String(locationInfo.user_id), {
    role: userInfo?.role,
    is_company_admin: !!userInfo?.isCompanyAdmin,
    company_id: companyId,
    company_name: locationInfo.company?.name,
    location_count: locationInfo.locations?.length || 0,
  });

  if (companyId) {
    posthog.group('company', String(companyId), {
      name: locationInfo.company?.name,
    });
  }

  // Attach to every subsequent event so all breakdowns work without extra args
  posthog.register({
    company_id: companyId,
    role: userInfo?.role,
    is_company_admin: !!userInfo?.isCompanyAdmin,
  });
};

export const resetAnalytics = () => {
  if (!enabled) return;
  posthog.reset();
};

// PostHog persists the opt-out flag in browser storage, so it survives reloads.
// Impersonation opts out; a normal login has to opt back in or that machine
// stays permanently untracked after one admin session.
export const optOutAnalytics = () => {
  if (!enabled) return;
  posthog.opt_out_capturing();
};

export const optInAnalytics = () => {
  if (!enabled) return;
  posthog.opt_in_capturing();
};

export const track = (event, properties = {}) => {
  if (!enabled) return;
  posthog.capture(event, properties);
};
